"use client";

import React, { useEffect, useState, Suspense } from "react";
import WaveTop from "../components/wave/WaveTop";
import "./style.css";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import Button from "../components/Button/Button";
import { deportesAll } from "../api/controllers/deportes";
import { obtenerLocalidades } from "../api/controllers/localidades";
import { obtenerPaises } from "../api/controllers/paises";
import CardSearch from "../components/CardSearch/CardSearch";
import { useSearchParams } from "next/navigation";
import TabType from "../components/Tab";
import { RotateCcw } from "lucide-react";
import Preload from "../components/Preload/Preload";

const PageContent = () => {
  const searchParams = useSearchParams();
  const [country, setCountry] = useState<string>(
    searchParams.get("country") || ""
  );
  const [countryId, setCountryId] = useState<number>(
    parseInt(searchParams.get("country") || "0")
  );
  const [sport, setSport] = useState<string>(searchParams.get("sport") || "");
  const [location, setLocation] = useState<string>(
    searchParams.get("location") || ""
  );
  const [selectedType, setSelectedType] = useState<string>("escuela");
  const [error, setError] = useState<string | null>(null);
  const [loading, setLoading] = useState(false);

  const [deportes, setDeportes] = useState<any[]>([]);
  const [paises, setPaises] = useState<any[]>([]);
  const [localidades, setLocalidades] = useState<any[]>([]);

  // Estado para almacenar los datos de búsqueda
  const [searchData, setSearchData] = useState<{
    deporte: number;
    pais: number;
    localidad: number;
  }>({
    deporte: parseInt(searchParams.get("sport") || "0"),
    pais: parseInt(searchParams.get("country") || "0"),
    localidad: parseInt(searchParams.get("location") || "0"),
  });

  useEffect(() => {
    if (searchData.deporte || searchData.pais || searchData.localidad) {
      document.getElementById("search")?.scrollIntoView({ behavior: "smooth" });
    }
  }, [searchData]);

  const fetchPaises = async () => {
    try {
      const data = await obtenerPaises();
      setPaises(data);
    } catch (err) {
      setError("Hubo un error al obtener los paises.");
    }
  };

  const fetchLocalidades = async (paisId: number) => {
    try {
      const data = await obtenerLocalidades(paisId);
      setLocalidades(data);
    } catch (err) {
      setError("Hubo un error al obtener las localidades.");
    }
  };

  const fetchDeportes = async () => {
    try {
      const data = await deportesAll();
      setDeportes(data);
    } catch (err) {
      setError("Hubo un error al obtener los deportes.");
    }
  };

  useEffect(() => {
    fetchPaises();
    if (countryId > 0) {
      fetchLocalidades(countryId);
    }
    fetchDeportes();
  }, [countryId]);

  useEffect(() => {
    if (searchParams.get("location")) {
      setLocation(searchParams.get("location") || "");
    }
  }, [localidades]);

  const handleSearch = () => {
    setLoading(true);
    setSearchData({
      deporte: parseInt(sport) || 0,
      pais: countryId > 0 ? countryId : 0,
      localidad: parseInt(location) || 0,
    });
    setTimeout(() => {
      setLoading(false);
    }, 1000);
  };

  const handleReset = () => {
    setLoading(true);
    setSport("");
    setCountry("");
    setLocation("");
    setCountryId(0);
    setSearchData({
      deporte: 0,
      pais: 0,
      localidad: 0,
    });
    setTimeout(() => {
      setLoading(false);
    }, 1000);
  };

  useEffect(() => {
    if (!searchData.deporte && !searchData.localidad && !searchData.pais) {
      setSearchData({
        deporte: 0,
        pais: 0,
        localidad: 0,
      });
    }
  }, []);

  return (
    <div className="min-h-screen w-full relative z-[10]">
      <div className="search_header">
        <div
          className={`flex justify-center relative items-center flex-col w-full lg:min-w-[55vw] gap-[3.5rem]`}
        >
          <div
            className={`box-text-surfBold flex justify-center flex-col w-full text-[2.5rem] tracking-[1.2px] items-center text-[#ffffff] uppercase font-bold`}
          >
            <h3 className="font-black text-[2.5rem] leading-snug mt-4 lg:mt-10 lg:text-[4rem] text-center">
              Buscá tu clase{" "}
            </h3>
          </div>

          <div className="w-full flex justify-center items-center flex-col lg:flex-row mx-auto gap-[1rem] p-4 px-0 rounded-lg relative z-[999] lg:max-w-[56rem]">
            <Select
              name="sport"
              onValueChange={(value) => setSport(value)}
              value={sport}
            >
              <SelectTrigger className="md:w-[70%] lg:min-w-[15rem]  lg:w-[15rem] bg-[#E8EDF4] border-none text-[#1B62BF]">
                <SelectValue placeholder="Deportes" />
              </SelectTrigger>
              <SelectContent>
                {deportes.map((deporte) => (
                  <SelectItem
                    key={deporte.deporteId}
                    value={deporte.deporteId.toString()}
                  >
                    {deporte.nombre}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>

            <Select
              name="country"
              onValueChange={(value) => {
                const selectedPais = paises.find(
                  (p) => p.id.toString() === value
                );
                if (selectedPais) {
                  setCountryId(selectedPais.id);
                  setCountry(value);
                }
              }}
              value={country}
            >
              <SelectTrigger className="md:w-[70%]  lg:min-w-[15rem]  lg:w-[15rem] bg-[#E8EDF4] border-none text-[#1B62BF]">
                <SelectValue placeholder="País" />
              </SelectTrigger>
              <SelectContent>
                {paises.map((pais) => (
                  <SelectItem key={pais.id} value={pais.id.toString()}>
                    {pais.nombrePais}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>

            <Select
              name="location"
              onValueChange={(value) => setLocation(value)}
              value={location}
            >
              <SelectTrigger className="md:w-[70%]  lg:min-w-[15rem]  lg:w-[15rem] bg-[#E8EDF4] border-none text-[#1B62BF]">
                <SelectValue placeholder="Localidad" />
              </SelectTrigger>
              <SelectContent>
                {localidades.map((local) => (
                  <SelectItem
                    key={local.localidadId}
                    value={local.localidadId.toString()}
                  >
                    {local.nombreLocalidad}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>

            <div className="flex gap-2 w-full md:w-[70%] lg:min-w-[15rem]">
              <button
                onClick={handleReset}
                className="relative inline-flex items-center gap-2 px-4 py-1 font-semibold text-blue-50 rounded-xl overflow-hidden hover:opacity-90 transition-opacity before:absolute before:top-4 before:left-1/2 before:-translate-x-1/2 before:w-[100px] before:h-[100px] before:rounded-full before:bg-gradient-to-b before:from-blue-50/10 before:blur-xl cursor-pointer bg-gradient-to-tr from-blue-100/0 via-blue-100/0 to-blue-100/0 ring-2 ring-[#d3e8f718]"
              >
                <RotateCcw className="w-8 h-8" color="#1B62BF" />
              </button>
              <Button
                title="BUSCAR"
                onClick={handleSearch}
                className="w-full h-[3.4rem] text-center flex justify-center items-center rounded-xl bg-gradient-to-tr from-blue-500 via-blue-500 to-blue-300"
              />
            </div>
          </div>
        </div>
      </div>

      <div className="w-full min-h-screen relative px-4 ">
        <WaveTop className="wave_classes" />

        <TabType
          setSelectedType={setSelectedType}
          selectedType={selectedType}
        />

        <CardSearch
          selectedType={selectedType}
          searchData={searchData}
          loading={loading}
        />
      </div>
    </div>
  );
};

const PageClases = () => {
  return (
    <Suspense fallback={<Preload />}>
      <PageContent />
    </Suspense>
  );
};

export default PageClases;
